import {
  createEmptyDeliveryGroup,
  makeStopUid
} from './deliveryGroupModel';
import { establishMissingStopPositions } from './editorStopOrdering';
import type {
  DeliveryGroupDraft,
  DeliveryGroupStopDraft,
  RouteApplicationGroupRef,
  StopExecutionLockState
} from './deliveryGroupTypes';

/** Stop row from GET /routes/:id/view structure.stops (same source as Route Card). */
export type ViewStructureStop = {
  id?: number | null;
  type?: string | null;
  position?: number | null;
  pointType?: string | null;
  pointId?: number | null;
  parentLoadingStopId?: number | null;
  groupId?: number | null;
  address?: string | null;
  city?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  plannedAt?: string | null;
  actualAt?: string | null;
  notes?: string | null;
  status?: string | null;
  clientExecutionUid?: string | null;
  stopUid?: string | null;
};

export function resolveViewStopUid(
  stop: Pick<ViewStructureStop, 'id' | 'clientExecutionUid' | 'stopUid'>
): string {
  const fromApi = String(stop.clientExecutionUid ?? stop.stopUid ?? '').trim();
  if (fromApi) return fromApi;
  if (stop.id != null && Number.isFinite(Number(stop.id))) return `db-${stop.id}`;
  return makeStopUid();
}

export type StopExecutionProjectionFields = {
  executionState: string;
  lockState: StopExecutionLockState;
  isCompleted: boolean;
};

/** Minimal slice of GET /routes/:id executionHydration for editor lock hydration. */
export type RouteEditorExecutionHydration = {
  stops?: Array<{ stopId: number; executionStopUid: string }>;
  routeExecutionProjection?: {
    stopProgress: Array<{
      executionStopUid: string;
      executionState: string;
      isCompleted: boolean;
      lockState: StopExecutionLockState;
    }>;
  };
};

export function buildStopExecutionFieldsByStopId(
  hydration?: RouteEditorExecutionHydration | null
): Map<number, StopExecutionProjectionFields> {
  const result = new Map<number, StopExecutionProjectionFields>();
  if (!hydration?.routeExecutionProjection?.stopProgress?.length) return result;

  const byUid = new Map(
    hydration.routeExecutionProjection.stopProgress.map((row) => [row.executionStopUid, row])
  );
  for (const graphStop of hydration.stops ?? []) {
    const progress = byUid.get(graphStop.executionStopUid);
    if (!progress) continue;
    result.set(Number(graphStop.stopId), {
      executionState: progress.executionState,
      lockState: progress.lockState,
      isCompleted: progress.isCompleted
    });
  }
  return result;
}

function viewStopToGroupDraft(
  stop: ViewStructureStop,
  groupUid: string,
  executionFields?: StopExecutionProjectionFields | null
): DeliveryGroupStopDraft {
  const type = String(stop.type || '').toLowerCase() === 'unloading' ? 'unloading' : 'loading';
  return {
    uid: resolveViewStopUid(stop),
    id: stop.id != null ? Number(stop.id) : null,
    groupUid,
    type,
    position: stop.position != null ? Number(stop.position) : undefined,
    pointType: (stop.pointType ?? null) as DeliveryGroupStopDraft['pointType'],
    pointId: stop.pointId != null ? Number(stop.pointId) : null,
    parentLoadingStopId: stop.parentLoadingStopId != null ? Number(stop.parentLoadingStopId) : null,
    parentLoadingPosition: null,
    groupId: stop.groupId != null ? Number(stop.groupId) : null,
    address: stop.address ?? null,
    city: stop.city ?? null,
    contactPerson: stop.contactPerson ?? null,
    phone: stop.phone ?? null,
    plannedAt: stop.plannedAt ?? null,
    actualAt: stop.actualAt ?? null,
    notes: stop.notes ?? null,
    status: stop.status ?? null,
    executionState: executionFields?.executionState ?? null,
    lockState: executionFields?.lockState ?? 'FUTURE',
    isCompleted: executionFields?.isCompleted ?? false
  };
}

function createGroupsFromApplicationRefs(
  applicationGroups?: RouteApplicationGroupRef[]
): DeliveryGroupDraft[] {
  if (!applicationGroups?.length) {
    return [
      createEmptyDeliveryGroup({
        sortOrder: 0,
        isDefault: true,
        loadingPosition: 0,
        label: 'Группа маршрута 1'
      })
    ];
  }

  const sorted = [...applicationGroups].sort(
    (a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0)
  );
  const hasDefault = sorted.some((g) => g.isDefault);

  return sorted.map((ag, idx) =>
    createEmptyDeliveryGroup({
      sortOrder: ag.sortOrder ?? idx,
      isDefault: ag.isDefault ?? (!hasDefault && idx === 0),
      loadingPosition: ag.isDefault || (!hasDefault && idx === 0) ? 0 : idx,
      groupId: ag.id,
      applicationId: ag.applicationId ?? null,
      label: ag.label ?? `Группа маршрута ${idx + 1}`
    })
  );
}

function sortStopsInGroup(group: DeliveryGroupDraft): DeliveryGroupDraft {
  const byPos = (a: DeliveryGroupStopDraft, b: DeliveryGroupStopDraft) =>
    Number(a.position ?? 0) - Number(b.position ?? 0);
  const loadingStops = [...group.loadingStops].sort(byPos);
  const unloadingStops = [...group.unloadingStops].sort(byPos);
  const primaryLoadingPos = loadingStops[0]?.position;
  const loadingPosition =
    group.isDefault && (primaryLoadingPos === 0 || primaryLoadingPos === undefined)
      ? 0
      : primaryLoadingPos ?? group.loadingPosition;
  return { ...group, loadingStops, unloadingStops, loadingPosition };
}

type ResolveContext = {
  groupById: Map<number, DeliveryGroupDraft>;
  defaultGroup: DeliveryGroupDraft;
  groups: DeliveryGroupDraft[];
  stopById: Map<number, ViewStructureStop>;
  assignedGroupByStopId: Map<number, DeliveryGroupDraft>;
};

function resolveGroupForStop(stop: ViewStructureStop, ctx: ResolveContext): DeliveryGroupDraft {
  const stopId = stop.id != null ? Number(stop.id) : NaN;
  if (Number.isFinite(stopId) && ctx.assignedGroupByStopId.has(stopId)) {
    return ctx.assignedGroupByStopId.get(stopId)!;
  }

  const type = String(stop.type || '').toLowerCase();
  const groupId = stop.groupId != null ? Number(stop.groupId) : null;
  if (groupId != null && ctx.groupById.has(groupId)) {
    return ctx.groupById.get(groupId)!;
  }

  if (type === 'loading') {
    if (Number(stop.position ?? 0) === 0) return ctx.defaultGroup;
    const byLoadingPos = ctx.groups.find(
      (g) => !g.isDefault && g.loadingPosition === Number(stop.position)
    );
    if (byLoadingPos) return byLoadingPos;
    return ctx.defaultGroup;
  }

  if (stop.parentLoadingStopId != null) {
    const parent = ctx.stopById.get(Number(stop.parentLoadingStopId));
    if (parent) {
      return resolveGroupForStop(parent, ctx);
    }
  }

  return ctx.defaultGroup;
}

/**
 * Hydrate delivery groups from view.structure.stops (Route Card source of truth).
 * Preserves every stop; prefers groupId over parentLoadingPosition heuristics.
 */
export function buildDeliveryGroupsFromViewStops(params: {
  viewStops: ViewStructureStop[];
  applicationGroups?: RouteApplicationGroupRef[];
  uidByStopId?: Map<number, string>;
  executionHydration?: RouteEditorExecutionHydration | null;
}): DeliveryGroupDraft[] {
  const executionByStopId = buildStopExecutionFieldsByStopId(params.executionHydration);
  const sorted = [...params.viewStops].sort(
    (a, b) => Number(a.position ?? 0) - Number(b.position ?? 0)
  );

  const groups = createGroupsFromApplicationRefs(params.applicationGroups);
  const groupById = new Map<number, DeliveryGroupDraft>();
  for (const g of groups) {
    if (g.groupId != null) groupById.set(Number(g.groupId), g);
  }

  const defaultGroup =
    groups.find((g) => g.isDefault) ?? groups[0] ?? createEmptyDeliveryGroup({ isDefault: true, sortOrder: 0, loadingPosition: 0 });

  const stopById = new Map<number, ViewStructureStop>();
  for (const s of sorted) {
    if (s.id != null) stopById.set(Number(s.id), s);
  }

  const ctx: ResolveContext = {
    groupById,
    defaultGroup,
    groups,
    stopById,
    assignedGroupByStopId: new Map()
  };

  const groupBuckets = new Map<string, DeliveryGroupDraft>();
  for (const g of groups) {
    groupBuckets.set(g.uid, { ...g, loadingStops: [], unloadingStops: [] });
  }

  for (const raw of sorted) {
    const enriched: ViewStructureStop = {
      ...raw,
      clientExecutionUid:
        raw.id != null && params.uidByStopId?.has(Number(raw.id))
          ? params.uidByStopId.get(Number(raw.id))
          : raw.clientExecutionUid,
      stopUid:
        raw.id != null && params.uidByStopId?.has(Number(raw.id))
          ? params.uidByStopId.get(Number(raw.id))
          : raw.stopUid
    };

    const targetMeta = resolveGroupForStop(enriched, ctx);
    const bucket = groupBuckets.get(targetMeta.uid) ?? groupBuckets.get(defaultGroup.uid)!;
    const executionFields =
      enriched.id != null ? executionByStopId.get(Number(enriched.id)) ?? null : null;
    const draft = viewStopToGroupDraft(enriched, bucket.uid, executionFields);

    if (draft.type === 'loading') {
      bucket.loadingStops.push(draft);
    } else {
      bucket.unloadingStops.push(draft);
    }

    if (enriched.id != null) {
      ctx.assignedGroupByStopId.set(Number(enriched.id), bucket);
    }
  }

  const sortedGroups = Array.from(groupBuckets.values())
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(sortStopsInGroup);

  return establishMissingStopPositions(sortedGroups);
}

/** After view hydration — ensure every stop draft has draft.position. */
export function finalizeHydratedDeliveryGroups(groups: DeliveryGroupDraft[]): DeliveryGroupDraft[] {
  return establishMissingStopPositions(groups);
}

export function countStopsInDeliveryGroups(groups: DeliveryGroupDraft[]): number {
  return groups.reduce(
    (total, g) => total + g.loadingStops.length + g.unloadingStops.length,
    0
  );
}

export function collectStopUidByIdFromGroups(
  groups: DeliveryGroupDraft[]
): Map<number, string> {
  const map = new Map<number, string>();
  for (const g of groups) {
    for (const s of [...g.loadingStops, ...g.unloadingStops]) {
      if (s.id != null) map.set(Number(s.id), s.uid);
    }
  }
  return map;
}

export function mergeViewStopsWithRouteStopUids(
  viewStops: ViewStructureStop[],
  routeStops: Array<{ id?: number; clientExecutionUid?: string; stopUid?: string }>
): { mergedStops: ViewStructureStop[]; uidByStopId: Map<number, string> } {
  const uidByStopId = new Map<number, string>();
  for (const s of routeStops) {
    if (s.id == null) continue;
    uidByStopId.set(Number(s.id), resolveViewStopUid(s));
  }

  const mergedStops = viewStops.map((vs) => {
    const id = vs.id != null ? Number(vs.id) : NaN;
    if (!Number.isFinite(id)) return vs;
    const uid = uidByStopId.get(id);
    return uid ? { ...vs, clientExecutionUid: uid, stopUid: uid } : vs;
  });

  return { mergedStops, uidByStopId };
}
